// DOM glue: HUD updates, the account bar buttons, and the email / one-time-code
// modal flow. All element lookups are cached once at construction. The class is
// driven by main.ts (which forwards server events) and talks back to the server
// through the Net instance it is given.

import { isValidEmail, sanitizeName, MAX_NAME_LEN, KICK_BAN_MS } from "@mmo/shared";
import type { Purpose } from "@mmo/shared";
import type { Net, ConnStatus } from "./net";
import * as storage from "./storage";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error("missing #" + id);
  return node as T;
}

const STATUS_TEXT: Record<ConnStatus, string> = {
  connecting: "reconnecting…",
  connected: "connected",
  disconnected: "disconnected",
};

export class UI {
  private readonly net: Net;

  // Cached HUD nodes.
  private readonly statusEl = el<HTMLSpanElement>("hud-status");
  private readonly countEl = el<HTMLElement>("hud-count");
  private readonly pingEl = el<HTMLElement>("hud-ping");
  private readonly goldEl = el<HTMLElement>("hud-gold");
  private readonly accountNameEl = el<HTMLElement>("account-name");
  private readonly settingsBtn = el<HTMLButtonElement>("settings-btn");
  private readonly settings = el<HTMLDivElement>("settings");

  // Connection + kick-countdown state for the gate.
  private status: ConnStatus = "connecting";
  private ready = false; // true once the world is loaded + self positioned
  private kickedUntil = 0;
  private kickTicker = 0;

  // Cached modal nodes.
  private readonly modal = el<HTMLDivElement>("modal");
  private readonly modalTitle = el<HTMLHeadingElement>("modal-title");
  private readonly stepEmail = el<HTMLDivElement>("modal-step-email");
  private readonly stepCode = el<HTMLDivElement>("modal-step-code");
  private readonly stepDone = el<HTMLDivElement>("modal-step-done");
  private readonly emailHelp = el<HTMLParagraphElement>("modal-email-help");
  private readonly emailInput = el<HTMLInputElement>("modal-email");
  private readonly codeInput = el<HTMLInputElement>("modal-code");
  private readonly doneMsg = el<HTMLParagraphElement>("modal-done-msg");
  private readonly errorEl = el<HTMLParagraphElement>("modal-error");

  // Login / connection gate.
  private readonly gate = el<HTMLDivElement>("gate");
  private readonly gateStatus = el<HTMLParagraphElement>("gate-status");
  private readonly gateActions = el<HTMLDivElement>("gate-actions");
  private readonly goldAmount = el<HTMLElement>("gold-amount");
  // True once the player has chosen to enter (clicked Continue as guest). Until
  // then we don't auto-connect — the landing screen waits for an explicit tap.
  private entering = false;

  // Active modal purpose ("bind" | "signin").
  private purpose: Purpose = "bind";
  private pendingEmail = "";

  constructor(net: Net) {
    this.net = net;
  }

  /** Wire all event listeners. Call once after construction. */
  init(): void {
    el<HTMLButtonElement>("btn-setname").addEventListener("click", () => this.changeName());
    el<HTMLButtonElement>("btn-link").addEventListener("click", () => this.openModal("bind"));
    el<HTMLButtonElement>("btn-signin").addEventListener("click", () => this.openModal("signin"));
    el<HTMLButtonElement>("gate-signin").addEventListener("click", () => this.openModal("signin"));
    // Explicit entry: connect only when the player chooses to play.
    el<HTMLButtonElement>("gate-play").addEventListener("click", () => {
      this.entering = true;
      this.net.connect();
      this.renderGate();
    });

    // Settings gear toggles its panel; Escape / picking an action closes it.
    this.settingsBtn.addEventListener("click", () => {
      this.settings.hidden = !this.settings.hidden;
    });
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.settings.hidden = true;
    });

    el<HTMLButtonElement>("modal-email-submit").addEventListener("click", () => this.submitEmail());
    el<HTMLButtonElement>("modal-code-submit").addEventListener("click", () => this.submitCode());

    // Enter-to-submit on the inputs.
    this.emailInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") this.submitEmail();
    });
    this.codeInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") this.submitCode();
    });
    // Keep the code field numeric.
    this.codeInput.addEventListener("input", () => {
      this.codeInput.value = this.codeInput.value.replace(/\D/g, "").slice(0, 6);
    });

    // Any element flagged data-close dismisses the modal.
    for (const node of this.modal.querySelectorAll("[data-close]")) {
      node.addEventListener("click", () => this.closeModal());
    }
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !this.modal.hidden) this.closeModal();
    });
  }

  // -- HUD ------------------------------------------------------------------
  setStatus(status: ConnStatus): void {
    this.status = status;
    this.statusEl.textContent = STATUS_TEXT[status];
    this.statusEl.className = "hud-status hud-status--" + status;
    if (status !== "connected") this.ready = false; // re-loading on (re)connect
    else {
      this.kickedUntil = 0;
      this.stopKickTicker();
    }
    this.renderGate();
  }

  /** Mark the game ready (world loaded + self positioned). Until this, the gate
   *  stays up even after the socket connects, hiding the init flash. */
  setReady(ready: boolean): void {
    this.ready = ready;
    this.renderGate();
  }

  /** The login gate covers the game until we're connected AND loaded, and shows
   *  a rejoin countdown while a kick ban is in effect. */
  private renderGate(): void {
    if (this.status === "connected" && this.ready) {
      this.gate.hidden = true;
      return;
    }
    this.gate.hidden = false;
    // Landing screen: before the player chooses to enter, show the action
    // buttons and no status. Once entering, show connection status, no buttons.
    this.gateActions.hidden = this.entering;
    if (!this.entering) {
      this.gateStatus.textContent = "";
      return;
    }
    const remain = this.kickedUntil - Date.now();
    if (remain > 0) {
      this.gateStatus.textContent = "Kicked — rejoin in " + Math.ceil(remain / 1000) + "s";
    } else if (this.status === "connected") {
      this.gateStatus.textContent = "Loading…"; // connected, awaiting world
    } else {
      this.gateStatus.textContent =
        this.status === "connecting" ? "Connecting…" : "Disconnected — reconnecting…";
    }
  }

  private startKickTicker(): void {
    if (this.kickTicker) return;
    this.kickTicker = window.setInterval(() => {
      this.renderGate();
      if (this.kickedUntil <= Date.now()) this.stopKickTicker();
    }, 500);
  }

  private stopKickTicker(): void {
    if (this.kickTicker) {
      window.clearInterval(this.kickTicker);
      this.kickTicker = 0;
    }
  }

  setCount(n: number): void {
    this.countEl.textContent = String(n);
  }

  setPing(ms: number | null): void {
    this.pingEl.textContent = ms === null ? "–" : String(ms);
  }

  // HP is shown as a bar above the player's body, so there's no HUD readout.
  setHp(_hp: number, _maxHp: number): void {
    /* no-op */
  }

  setGold(amount: number): void {
    this.goldEl.textContent = String(amount);
    this.goldAmount.textContent = String(amount);
  }

  setName(name: string): void {
    this.accountNameEl.textContent = name;
  }

  // -- Name change ----------------------------------------------------------
  private changeName(): void {
    this.settings.hidden = true;
    const current = storage.getName() ?? this.accountNameEl.textContent ?? "";
    const raw = window.prompt("Choose a display name:", current);
    if (raw === null) return;
    const name = sanitizeName(raw, MAX_NAME_LEN);
    if (!name) return;
    storage.setName(name);
    this.setName(name);
    // Send live + update the hello identity so a reconnect keeps the name.
    this.net.send({ t: "set_name", name });
    this.net.setIdentity(storage.getToken(), name);
  }

  // -- Email / code modal ---------------------------------------------------
  private openModal(purpose: Purpose): void {
    this.settings.hidden = true;
    this.purpose = purpose;
    this.pendingEmail = "";
    this.modalTitle.textContent =
      purpose === "bind" ? "Link an email" : "Sign in on this device";
    this.emailHelp.textContent =
      purpose === "bind"
        ? "Link an email so you can sign in from any device."
        : "Enter the email linked to your account to sign in here.";
    this.showStep("email");
    this.clearError();
    this.emailInput.value = "";
    this.codeInput.value = "";
    this.modal.hidden = false;
    this.emailInput.focus();
  }

  private closeModal(): void {
    this.modal.hidden = true;
  }

  private submitEmail(): void {
    const email = this.emailInput.value.trim();
    if (!isValidEmail(email)) {
      this.showError("Please enter a valid email address.");
      return;
    }
    this.pendingEmail = email;
    this.clearError();
    this.net.send({ t: "email_request", email, purpose: this.purpose });
  }

  private submitCode(): void {
    const code = this.codeInput.value.trim();
    if (code.length !== 6) {
      this.showError("Enter the 6-digit code.");
      return;
    }
    this.clearError();
    this.net.send({
      t: "email_verify",
      email: this.pendingEmail,
      code,
      purpose: this.purpose,
    });
  }

  // -- Server-driven modal transitions (called from main) -------------------
  onEmailSent(_purpose: Purpose): void {
    this.clearError();
    this.showStep("code");
    this.codeInput.value = "";
    this.codeInput.focus();
  }

  onEmailBound(email: string): void {
    this.doneMsg.textContent = "Email linked: " + email;
    this.clearError();
    this.showStep("done");
  }

  /** Cross-device sign-in succeeded: persist the new token and reload. */
  onSignedIn(token: string): void {
    storage.setToken(token);
    location.reload();
  }

  /** Show a server error; if the modal is open it appears inside it. */
  onError(code: string, message: string): void {
    if (code === "kicked") {
      // Start (or keep) the rejoin countdown shown on the gate. Don't extend it
      // when later reconnect attempts are also rejected during the ban window.
      const now = Date.now();
      if (this.kickedUntil <= now) this.kickedUntil = now + KICK_BAN_MS;
      this.startKickTicker();
      this.renderGate();
      return;
    }
    if (!this.modal.hidden) {
      this.showError(message);
    } else {
      // Brief, non-blocking surfacing for out-of-modal errors.
      console.warn("[server error]", message);
    }
  }

  private showStep(step: "email" | "code" | "done"): void {
    this.stepEmail.hidden = step !== "email";
    this.stepCode.hidden = step !== "code";
    this.stepDone.hidden = step !== "done";
  }

  private showError(message: string): void {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  private clearError(): void {
    this.errorEl.textContent = "";
    this.errorEl.hidden = true;
  }
}
